import React, { useRef, ReactElement, MutableRefObject } from 'react';
import { RendererProvider, ThemeProvider } from 'react-fela';
import PropTypes from 'prop-types';
import { createRenderer, IRenderer, TPlugin, TEnhancer } from 'fela';
import { theme as dmTheme } from '../..';
import config from './styleConfig';
import ThemeProps from '../ThemeProps';

declare global {
  interface Window {
    felaRenderer: IRenderer;
  }
}

let rendererRef: React.MutableRefObject<IRenderer> | undefined;
/**
 * @deprecated introduced with version 4.0.0 to be used as a quick-fix; please prefer using the useFela-Hook!
 */
export const getRenderer = (): IRenderer | undefined => rendererRef?.current;

const isClient =
  typeof window !== 'undefined' &&
  window.document &&
  process.env.NODE_ENV !== 'test';

export interface StyleProviderProps {
  /**
   * Prepend a static prefix to every generated class and keyframe. Useful for server-side rendering
   */
  selectorPrefix?: string;
  /**
   * A list of plugins to process styles before rendering, see: https://fela.js.org/docs/latest/advanced/plugins
   */
  plugins?: Array<TPlugin>;
  /**
   * A list of enhancers to enhance the renderer, see: https://fela.js.org/docs/latest/advanced/enhancers
   */
  enhancers?: Array<TEnhancer>;
  /**
   * to overwrite the dm-Theme.
   * Default: dm-Theme
   */
  theme?: ThemeProps;
  /**
   * Nonce which should be used for style-nodes of fela
   */
  styleNodeAttributes?: { nonce?: string };
  children: ReactElement;
}

export const StyleProvider = ({
  children,
  selectorPrefix = '',
  plugins = [],
  enhancers = [],
  theme = dmTheme,
  styleNodeAttributes = {},
}: StyleProviderProps): JSX.Element => {
  // initially we use the imported default config, later we set our props on rendererRef
  rendererRef = useRef<IRenderer>(createRenderer(config));

  // use theme ref to avoid re-render problem in components that use the useFela hook
  const themeRef = useRef(theme);

  // set the global client-side fela renderer and reuse global atomic styles
  if (isClient) {
    if (!window.felaRenderer) {
      window.felaRenderer = rendererRef.current;
    } else {
      rendererRef.current = window.felaRenderer;
    }
  }

  if (selectorPrefix !== '') {
    // @ts-expect-error typing of fela is not yet complete
    rendererRef.current.selectorPrefix = selectorPrefix;
  }

  if (styleNodeAttributes?.nonce) {
    // @ts-expect-error typing of fela is not yet complete
    rendererRef.current.styleNodeAttributes.nonce = styleNodeAttributes.nonce;
  }

  if (plugins?.length > 0) {
    // @ts-expect-error typing of fela is not yet complete
    rendererRef.current.plugins.push(...plugins);
  }

  if (enhancers?.length > 0) {
    // @ts-expect-error in here rendererRef is never undefined
    enhancers.forEach(enhancer => enhancer(rendererRef.current));
  }

  return (
    <RendererProvider renderer={rendererRef.current}>
      <ThemeProvider overwrite theme={themeRef.current}>
        {children}
      </ThemeProvider>
    </RendererProvider>
  );
};

StyleProvider.propTypes = {
  selectorPrefix: PropTypes.string,
  theme: PropTypes.object,
  children: PropTypes.node,
  plugins: PropTypes.array,
  enhancers: PropTypes.arrayOf(PropTypes.func),
};

export default StyleProvider;
